import React, { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

import { Button, Table } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { PARTNERSHIPS_PARTNER, PARTNERSHIPS_USER } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  PartnershipsAdminPageDocument,
  PartnershipsAdminPartnerDocument,
  Status,
  usePartnershipsAdminPageQuery,
  usePartnershipsAdminPartnerQuery,
  usePartnershipsAdminPartnerCreateMutation,
  usePartnershipsAdminPartnerUpdateMutation,
  usePartnershipsAdminPartnerDeactivateMutation,
  usePartnershipsAdminPartnerActivateMutation,
  usePartnershipsAdminPartnerUserCreateMutation,
  usePartnershipsAdminPartnerUserDeactivateMutation,
  usePartnershipsAdminPartnerUserActivateMutation,
  usePartnershipsAdminPartnerUserRoleUpdateMutation,
} from '@admin/schema';

import './partners_admin.scss';

function formatCreatedAt(iso: string): string {
  try {
    return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED);
  } catch {
    return iso;
  }
}

export const PartnershipsAdmin: React.FC = () => {
  useTitle('Partnerships');
  const canCreatePartners = usePermission(PARTNERSHIPS_PARTNER.resource, PARTNERSHIPS_PARTNER.actions.create);
  const canManagePartners = usePermission(PARTNERSHIPS_PARTNER.resource, PARTNERSHIPS_PARTNER.actions.update);
  const canCreatePortalUsers = usePermission(PARTNERSHIPS_USER.resource, PARTNERSHIPS_USER.actions.create);
  const canManagePortalUsers = usePermission(PARTNERSHIPS_USER.resource, PARTNERSHIPS_USER.actions.update);

  const { data: pageData, loading: pageLoading } = usePartnershipsAdminPageQuery({
    client,
  });
  const partners = pageData?.partnershipsPartners ?? [];
  const catalog = pageData?.partnershipsPartnerApiEndpointCatalog ?? [];

  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [endpointIds, setEndpointIds] = useState<string[]>([]);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<'developer' | 'admin'>('developer');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedPartnerId && partners.length > 0) {
      setSelectedPartnerId(partners[0].id);
    }
  }, [partners, selectedPartnerId]);

  const { data: partnerData, loading: partnerLoading } = usePartnershipsAdminPartnerQuery({
    client,
    variables: { id: selectedPartnerId ?? '' },
    skip: !selectedPartnerId,
  });

  const partner = partnerData?.partnershipsPartner;

  useEffect(() => {
    const ids = partner?.enabledPartnerApiEndpointIds;
    if (ids !== undefined) {
      setEndpointIds(ids ?? []);
    }
  }, [partner?.id, partner?.enabledPartnerApiEndpointIds]);

  const [createPartner, { loading: creating }] = usePartnershipsAdminPartnerCreateMutation({ client });
  const [updatePartner, { loading: savingEndpoints }] = usePartnershipsAdminPartnerUpdateMutation({ client });
  const [deactivatePartner, { loading: deactivatingPartner }] = usePartnershipsAdminPartnerDeactivateMutation({
    client,
  });
  const [activatePartner, { loading: activatingPartner }] = usePartnershipsAdminPartnerActivateMutation({ client });
  const [createUser, { loading: creatingUser }] = usePartnershipsAdminPartnerUserCreateMutation({ client });
  const [deactivatePortalUser, { loading: deactivatingPortalUser }] = usePartnershipsAdminPartnerUserDeactivateMutation(
    { client },
  );
  const [activatePortalUser, { loading: activatingPortalUser }] = usePartnershipsAdminPartnerUserActivateMutation({
    client,
  });
  const [updatePortalUserRole, { loading: updatingPortalUserRole }] = usePartnershipsAdminPartnerUserRoleUpdateMutation(
    {
      client,
    },
  );

  const catalogIds = catalog.map((c) => c.id).sort();

  const refetchPartnerAdmin = (partnerId: string) => [
    { query: PartnershipsAdminPageDocument },
    { query: PartnershipsAdminPartnerDocument, variables: { id: partnerId } },
  ];

  const toggleEndpoint = (id: string) => {
    setEndpointIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const onSaveEndpoints = async () => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await updatePartner({
      variables: {
        input: {
          id: selectedPartnerId,
          enabledPartnerApiEndpointIds: endpointIds,
        },
      },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerUpdate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not update partner.');
    }
  };

  const onCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const name = newPartnerName.trim();
    if (!name) {
      setFormError('Partner name is required.');
      return;
    }
    const result = await createPartner({
      variables: { input: { name } },
      refetchQueries: [{ query: PartnershipsAdminPageDocument }],
    });
    const payload = result.data?.partnershipsPartnerCreate;
    if (payload?.status === Status.Ok && payload.partner?.id) {
      setNewPartnerName('');
      setSelectedPartnerId(payload.partner.id);
    } else if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not create partner.');
    }
  };

  const onCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await createUser({
      variables: {
        input: {
          partnerID: selectedPartnerId,
          name: userName.trim(),
          email: userEmail.trim(),
          role: userRole,
        },
      },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerUserCreate;
    if (payload?.status === Status.Ok) {
      setUserName('');
      setUserEmail('');
      setUserRole('developer');
    } else if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not create user.');
    }
  };

  const allSelected = catalogIds.length > 0 && catalogIds.every((id) => endpointIds.includes(id));

  const onDeactivatePartner = async () => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await deactivatePartner({
      variables: { id: selectedPartnerId },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerDeactivate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not deactivate partner.');
    }
  };

  const onActivatePartner = async () => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await activatePartner({
      variables: { id: selectedPartnerId },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerActivate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not activate partner.');
    }
  };

  const onDeactivatePortalUser = async (userId: string) => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await deactivatePortalUser({
      variables: { id: userId },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerUserDeactivate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not deactivate user.');
    }
  };

  const onActivatePortalUser = async (userId: string) => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await activatePortalUser({
      variables: { id: userId },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerUserActivate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not activate user.');
    }
  };

  const onUpdatePortalUserRole = async (userId: string, role: string) => {
    if (!selectedPartnerId) return;
    setFormError(null);
    const result = await updatePortalUserRole({
      variables: { id: userId, role },
      refetchQueries: refetchPartnerAdmin(selectedPartnerId),
    });
    const payload = result.data?.partnershipsPartnerUserRoleUpdate;
    if (payload?.status === Status.Unprocessable) {
      setFormError(payload.error ?? 'Could not update role.');
    }
  };

  const partnerMutating = deactivatingPartner || activatingPartner;
  const portalUserMutating = deactivatingPortalUser || activatingPortalUser || updatingPortalUserRole;

  return (
    <Permission resource={PARTNERSHIPS_PARTNER.resource} action={PARTNERSHIPS_PARTNER.actions.read}>
      <div className="partnerships-admin">
        <div className="partnerships-admin__shell">
          <header className="partnerships-admin__page-header">
            <h1 className="partnerships-admin__title">Partnerships</h1>
            <p className="partnerships-admin__lede">
              Create integration partners, enable Partner API routes when the contract is ready, and provision users for
              the partner portal. New partners start with no API access until endpoints are enabled here.
            </p>
          </header>

          {formError ? (
            <div className="partnerships-admin__alert" role="alert">
              {formError}
            </div>
          ) : null}

          {canCreatePartners ? (
            <form className="partnerships-admin__toolbar" onSubmit={onCreatePartner}>
              <div className="partnerships-admin__toolbar-field">
                <span className="partnerships-admin__toolbar-label">New partner</span>
                <input
                  id="new-partner-name"
                  className="partnerships-admin__toolbar-input"
                  placeholder="Company or integration name"
                  value={newPartnerName}
                  onChange={(ev) => setNewPartnerName(ev.target.value)}
                  disabled={creating}
                  autoComplete="off"
                />
              </div>
              <Button kind="mint" type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Add partner'}
              </Button>
            </form>
          ) : null}

          {pageLoading ? (
            <Spinner />
          ) : (
            <div className="partnerships-admin__grid">
              <aside className="partnerships-admin__sidebar-card" aria-label="Partners">
                <div className="partnerships-admin__sidebar-head">Partners ({partners.length})</div>
                {partners.length === 0 ? (
                  <div className="partnerships-admin__empty">No partners yet — add one above.</div>
                ) : (
                  <ul className="partnerships-admin__partner-list">
                    {partners.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className={[
                            'partnerships-admin__partner-item',
                            p.id === selectedPartnerId ? 'partnerships-admin__partner-item--active' : '',
                            p.active === false ? 'partnerships-admin__partner-item--inactive' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => {
                            setSelectedPartnerId(p.id);
                            setFormError(null);
                          }}
                        >
                          <span className="partnerships-admin__partner-item-label">{p.name}</span>
                          {p.active === false ? (
                            <span className="partnerships-admin__partner-item-status">Inactive</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>

              <main className="partnerships-admin__main">
                {partnerLoading && selectedPartnerId ? (
                  <div className="partnerships-admin__card">
                    <div className="partnerships-admin__card-body">
                      <Spinner />
                    </div>
                  </div>
                ) : null}

                {partner && !partnerLoading ? (
                  <>
                    <section className="partnerships-admin__card" aria-labelledby="pa-api-title">
                      <div className="partnerships-admin__card-head">
                        <div>
                          <h2 className="partnerships-admin__hero-title">{partner.name}</h2>
                          <p className="partnerships-admin__card-kicker" id="pa-api-title">
                            Partner API access
                          </p>
                        </div>
                        <div className="partnerships-admin__card-head-aside">
                          <span className="partnerships-admin__badge">id {partner.id}</span>
                          {partner.active === false ? (
                            <span className="partnerships-admin__status partnerships-admin__status--inactive">
                              Inactive
                            </span>
                          ) : (
                            <span className="partnerships-admin__status partnerships-admin__status--active">
                              Active
                            </span>
                          )}
                          {canManagePartners ? (
                            partner.active !== false ? (
                              <Button
                                kind="danger"
                                type="button"
                                disabled={partnerMutating}
                                onClick={() => void onDeactivatePartner()}
                              >
                                {deactivatingPartner ? 'Deactivating…' : 'Deactivate partner'}
                              </Button>
                            ) : (
                              <Button
                                kind="mint"
                                type="button"
                                disabled={partnerMutating}
                                onClick={() => void onActivatePartner()}
                              >
                                {activatingPartner ? 'Activating…' : 'Activate partner'}
                              </Button>
                            )
                          ) : null}
                        </div>
                      </div>
                      <div className="partnerships-admin__card-body">
                        <p className="partnerships-admin__hint">
                          Select which documented Partner API routes this integration may call. Enforcement applies to{' '}
                          <code>api.</code> traffic using this partner&apos;s API keys.
                        </p>
                        {partner.active === false ? (
                          <p className="partnerships-admin__hint partnerships-admin__hint--warn" role="status">
                            This partner is inactive: API keys cannot call the Partner API and portal users are signed
                            out until you activate the partner again.
                          </p>
                        ) : null}
                        {canManagePartners ? (
                          <div className="partnerships-admin__actions">
                            <Button
                              kind="default"
                              type="button"
                              onClick={() => setEndpointIds([...catalogIds])}
                              disabled={allSelected || partner.active === false}
                            >
                              Enable all
                            </Button>
                            <Button
                              kind="default"
                              type="button"
                              onClick={() => setEndpointIds([])}
                              disabled={endpointIds.length === 0 || partner.active === false}
                            >
                              Clear all
                            </Button>
                            <Button
                              kind="primary"
                              type="button"
                              onClick={() => void onSaveEndpoints()}
                              disabled={savingEndpoints || partner.active === false}
                            >
                              {savingEndpoints ? 'Saving…' : 'Save changes'}
                            </Button>
                          </div>
                        ) : null}
                        <div className="partnerships-admin__endpoint-grid">
                          {catalog.map((entry) => (
                            <label key={entry.id} className="partnerships-admin__endpoint-row">
                              <input
                                type="checkbox"
                                checked={endpointIds.includes(entry.id)}
                                onChange={() => toggleEndpoint(entry.id)}
                                disabled={!canManagePartners || partner.active === false}
                              />
                              <span>
                                <span className="partnerships-admin__endpoint-id">{entry.id}</span>
                                <span className="partnerships-admin__endpoint-meta">{entry.railsKey}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </section>

                    <section className="partnerships-admin__card" aria-labelledby="pa-users-title">
                      <div className="partnerships-admin__card-head">
                        <h3 className="partnerships-admin__card-title" id="pa-users-title">
                          Portal users
                        </h3>
                      </div>
                      <div className="partnerships-admin__card-body">
                        <div className="partnerships-admin__table-wrap">
                          <Table responsive striped className="partnerships-admin__table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th className="partnerships-admin__th-actions"> </th>
                              </tr>
                            </thead>
                            <tbody>
                              {partner.users.map((u) => (
                                <tr key={u.id}>
                                  <td>{u.name}</td>
                                  <td>{u.email}</td>
                                  <td>
                                    <select
                                      id={`pu-role-${u.id}`}
                                      className="partnerships-admin__input partnerships-admin__role-select"
                                      aria-label={`Role for ${u.name}`}
                                      value={u.role}
                                      disabled={!canManagePortalUsers || portalUserMutating || partner.active === false}
                                      onChange={(ev) => void onUpdatePortalUserRole(u.id, ev.target.value)}
                                    >
                                      <option value="developer">Developer</option>
                                      <option value="admin">Admin</option>
                                    </select>
                                  </td>
                                  <td>{u.active === false ? 'Inactive' : 'Active'}</td>
                                  <td>{formatCreatedAt(u.createdAt)}</td>
                                  <td className="partnerships-admin__td-actions">
                                    {canManagePortalUsers ? (
                                      u.active === false ? (
                                        <Button
                                          kind="mint"
                                          type="button"
                                          disabled={portalUserMutating || partner.active === false}
                                          onClick={() => void onActivatePortalUser(u.id)}
                                        >
                                          {activatingPortalUser ? '…' : 'Activate'}
                                        </Button>
                                      ) : (
                                        <Button
                                          kind="danger"
                                          type="button"
                                          disabled={portalUserMutating}
                                          onClick={() => void onDeactivatePortalUser(u.id)}
                                        >
                                          {deactivatingPortalUser ? '…' : 'Deactivate'}
                                        </Button>
                                      )
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {canCreatePortalUsers ? (
                          <div className="partnerships-admin__add-user">
                            <h4 className="partnerships-admin__subhead">Add portal user</h4>
                            <p className="partnerships-admin__add-user-hint">
                              We email them a link to set their password and access the partner portal.
                            </p>
                            {partner.active === false ? (
                              <p className="partnerships-admin__hint partnerships-admin__hint--warn" role="status">
                                Activate the partner before adding portal users.
                              </p>
                            ) : null}
                            <form onSubmit={(e) => void onCreateUser(e)}>
                              <div className="partnerships-admin__form-grid">
                                <div className="partnerships-admin__field">
                                  <label htmlFor="pu-name">Name</label>
                                  <input
                                    id="pu-name"
                                    className="partnerships-admin__input"
                                    value={userName}
                                    onChange={(ev) => setUserName(ev.target.value)}
                                    required
                                    disabled={creatingUser || partner.active === false}
                                    autoComplete="off"
                                  />
                                </div>
                                <div className="partnerships-admin__field">
                                  <label htmlFor="pu-email">Email</label>
                                  <input
                                    id="pu-email"
                                    type="email"
                                    className="partnerships-admin__input"
                                    value={userEmail}
                                    onChange={(ev) => setUserEmail(ev.target.value)}
                                    required
                                    disabled={creatingUser || partner.active === false}
                                    autoComplete="off"
                                  />
                                </div>
                                <div className="partnerships-admin__field">
                                  <label htmlFor="pu-role">Role</label>
                                  <select
                                    id="pu-role"
                                    className="partnerships-admin__input"
                                    value={userRole}
                                    onChange={(ev) => setUserRole(ev.target.value as 'developer' | 'admin')}
                                    disabled={creatingUser || partner.active === false}
                                  >
                                    <option value="developer">Developer</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </div>
                                <div className="partnerships-admin__field">
                                  <label htmlFor="pu-submit" className="sr-only">
                                    Submit
                                  </label>
                                  <Button
                                    id="pu-submit"
                                    kind="primary"
                                    type="submit"
                                    disabled={creatingUser || partner.active === false}
                                  >
                                    {creatingUser ? 'Adding…' : 'Add user'}
                                  </Button>
                                </div>
                              </div>
                            </form>
                          </div>
                        ) : null}
                      </div>
                    </section>
                  </>
                ) : null}

                {!partner && !partnerLoading && selectedPartnerId ? (
                  <div className="partnerships-admin__card">
                    <div className="partnerships-admin__card-body">
                      <p className="partnerships-admin__hint">Partner could not be loaded.</p>
                    </div>
                  </div>
                ) : null}
              </main>
            </div>
          )}
        </div>
      </div>
    </Permission>
  );
};
